/**
 Copyright (c) 2019 Attic Creation
 Form 3.2
 */
 import classNames from "classnames";
 import makeStyles from "@material-ui/core/styles/makeStyles";
 import { cloneElement, ReactElement, useRef } from "react";
 import React from "react";
 import FormErrorText from "./form-error-text";
 import FormInputField from "./form-input";
 import MiniButton from "./mini-button";
 import PrimaryButton from "./primary-button";
 import FormSwitch from "./form-switch";
 import { IconButton } from "@material-ui/core";
 import DialogBottomButton from "../dialogs/dialog-bottom-button";
 import FormCheckbox from './form-checkbox';
 import SecondaryButton from "./secondary-button";
 import BudgetInput from "../../task-components/offers/budget-input";
 
 const useStyles = makeStyles({
     form : {
         padding: "0 !important",
         width: "100%",
         display: "flex",
         flexDirection: "column"
     },
     padding: {
         padding: "24px !important",
     }
 });
 
 function FormWrapper({ children, padding = false, initialValues = {}, onSubmit, onValueChange, autoComplete, style, reset, disableLoading = false }) {
   const classes = useStyles();
 
   // Loading state for form
   const [loading, setLoading] = React.useState(false);
 
   // Flag to show / hide error texts and highlights
   const [showError, setShowError] = React.useState(false);
 
   // Error object
   const [error, setError] = React.useState({});
 
   // All values in an form retrieved based on the [name] prop from [InputField]
   const [values, setValues] = React.useState(initialValues);
 
   // Form reference
   const formReference = useRef(null);
 
   // Submit is trigger on Form Submission which validates the fields based on given props.
   const submit = async (event) => {
     if (event && event.preventDefault) {
       event.preventDefault();
     }
 
     if (event && event.stopPropagation) {
       event.stopPropagation();
     }
 
     // Assuming all data is valid
     let validation = true;
 
     // Storing current error object
     let e = error;
 
     // Setting Show Error to false until validation is complete
     setShowError(false);
 
     let eventTarget = event.target;
 
     if (eventTarget.elements) {
       let elementsLength = eventTarget.elements.length;
       for (let i = 0; i < elementsLength; i++) {
         let element = eventTarget.elements[i] ;
         if (element.hasAttribute("name")) {
           switch (element.type) {
             case "checkbox":
               if(element.required && !element.checked){
                   e[element.name] = "empty";
                   validation = false
               } else{
                   e[element.name] = "";
               }
               break;
             case "email":
               if (element.required && !element.value.trim()) {
                 e[element.name] = "empty";
                 validation = false;
               } else if (!element.value.match(new RegExp(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/))) {
                 e[element.name] = "Please enter a valid Email Address.";
                 validation = false;
               } else {
                 e[element.name] = "";
               }
               break;
             case "date":
               if (element.required && !element.value) {
                 e[element.name] = "empty";
                 validation = false;
               } else {
                 e[element.name] = "";
               }
               break;
             case "tel":
               if (element.required && !element.value.trim()) {
                 e[element.name] = "empty";
                 validation = false;
               } else if (element.value.length !== 14) {
                 e[element.name] = "Please enter a valid Phone Number.";
                 validation = false;
               } else {
                 e[element.name] = "";
               }
               break;
             case "number":
             case "text":
             case "password":
             case "textarea":
               if (element.required && !element.value.trim()) {
                 e[element.name] = "empty";
                 validation = false;
               } else {
                 e[element.name] = "";
               }
               break;
             default:
               break;
           }
         }
       }
       await setError(e);
     }
 
     if (validation) {
       if (!disableLoading) await setLoading(true);
      if(onSubmit){
       await onSubmit(values)
         .then(async () => {
           if (reset) {
             if (formReference.current) formReference.current.reset();
             let v = {};
             for (let k in values) {
               if (values.hasOwnProperty(k)) v[k] = "";
             }
             setValues(v);
           }
           await setLoading(false);
           await setShowError(false);
         })
         .catch(async (err) => {
           if (typeof err === "string") {
             await setLoading(false);
             await setShowError(true);
             await setError({ text: err });
           } else {
             await setLoading(false);
             await setShowError(true);
             await setError(err);
           }
         });}
     } else {
       setLoading(false);
       setShowError(true);
     }
   };
 
   const handleChange = (event) => {
     let evenTarget = event.target ;
     setError({ ...error, [evenTarget.name]: "", text: "" });
     setValues({ ...values, [evenTarget.name]: evenTarget.type === "checkbox" ? evenTarget.checked : evenTarget.value });
     if (onValueChange) onValueChange({ ...values, [evenTarget.name]: evenTarget.type === "checkbox" ? evenTarget.checked : evenTarget.value });
   };
 
   const handleAlienChange = () => {
     setError({  });
   };
 
   let processElements = (reactElements) => {
     reactElements.forEach((el, index) => {
       let element = el ;
       if (element.type === FormInputField)
         reactElements[index] = cloneElement(element, {
           disabled: loading,
           onChange: handleChange,
           value: values[element.props.name] ?? undefined,
           error: showError ? error[element.props.name] : "",
           key: index,
         });
       else if (element.type === FormSwitch)
         reactElements[index] = cloneElement(element, {
           disabled: loading,
           onChange: handleChange,
           checked: values[element.props.name] ?? undefined,
           key: index,
         });
       else if (element.type === FormCheckbox)
         reactElements[index] = cloneElement(element, {
             disabled: loading, onChange: handleChange, checked: values[element.props.name] ?? false, error: showError ? error[element.props.name] : "",  key: index
         });
       else if (element.type === BudgetInput)
         reactElements[index] = cloneElement(element, {
             onChange: handleAlienChange, key: index
         });
       else if (element.type === PrimaryButton || element.type === SecondaryButton || element.type === MiniButton || element.type === IconButton || element.type === DialogBottomButton) {
        if (!element.props.isLoading) {
          reactElements[index] = cloneElement(element, {
            isLoading: loading,
            type: "submit",
            key: index,
          });
        }
       } else if (element.type === FormErrorText)
         reactElements[index] = cloneElement(element, {
           error: error,
           showError: showError,
           key: index,
         });
       else {
         if (element.props) {
           let possibleFields = React.Children.toArray(element.props.children);
           if (possibleFields.length > 0) {
             possibleFields = processElements(possibleFields);
           }
           reactElements[index] = cloneElement(element, {
             children: possibleFields,
           });
         }
       }
     });
     return reactElements;
   };
 
   let elements = React.Children.toArray(children);
   elements = processElements(elements);
 
   return (
     <form ref={formReference} onSubmit={submit} autoComplete={autoComplete} className={classNames({ [classes.form]: true, [classes.padding]: padding })} noValidate style={style}>
       {elements}
     </form>
   );
 }
 
 export default FormWrapper;
 